import type { PaletteColor } from "@trip/domain/palette";
import { Check, Heart, Undo2 } from "lucide-react";
import { UNCATEGORISED } from "../lib/category-icons.ts";
import type { Poi } from "../lib/pois.ts";
import { Likers, likedSaid } from "./PoiLikes.tsx";

/**
 * A place's name after its category's emoji, when the surface shows emoji at all (`emojis` is the
 * category id -> emoji map; 📍 for a place with none), or the name alone when it does not.
 */
export function poiName(p: Poi, emojis: Map<string, string> | undefined) {
  if (!emojis) return p.name;
  return (
    <>
      <span aria-hidden="true">
        {(p.categoryId !== null && emojis.get(p.categoryId)) || UNCATEGORISED.emoji}
      </span>{" "}
      {p.name}
    </>
  );
}

/**
 * One place in a list, on two lines. The name has the whole width of the first. The second has the
 * faces of those who like it at the left, a short line saying why it is worth a look in the middle
 * (it truncates, having already abbreviated), and at the right the controls, like and visited.
 *
 * The controls follow the handlers the host gives: with no `onLike` there is no like, with no
 * `onVisit` no visited, and a row given neither has nothing at its right. A row can only do what
 * its host gave it the means to do. The map's sheet gives both; the places page, which is for
 * editing, gives neither.
 *
 * Who likes a place is no longer printed: the face stack carries it as its accessible name.
 * It only draws: the page that owns the list owns the writes, and tells the row when one is in
 * flight (`busy`) or failed (`error`).
 */
export function PoiRow({
  poi,
  name,
  text,
  passed = false,
  closing = false,
  current = false,
  note = null,
  error = null,
  busy = false,
  settling = false,
  visited = false,
  open = null,
  me,
  colorOf,
  onLike,
  onVisit,
}: {
  poi: Poi;
  /** The name as the surface shows it: `poiName`. */
  name: React.ReactNode;
  /** The line under the name; nothing when empty. */
  text?: string | undefined;
  /** The place's window has closed for today. */
  passed?: boolean;
  /** The place's window is about to close. */
  closing?: boolean;
  /** The row a pin opened the list on. */
  current?: boolean;
  /** A line of the list's own note; the place view has the whole of it. */
  note?: string | null;
  /** A write on this row that did not reach the server, in words. */
  error?: string | null;
  /** A write is in flight: both controls wait for its answer. */
  busy?: boolean;
  /** The visited tick is inert for a moment after one landed: see `TICK_SETTLE_MS`. */
  settling?: boolean;
  /** The place has been visited: the tick takes it back instead. */
  visited?: boolean;
  /** What the name does, where it does anything. */
  open?: { label: string; run: (id: string) => void } | null;
  me: string | null;
  colorOf?: ((userId: string) => PaletteColor | null) | undefined;
  /** Likes or withdraws the like; with none given the row offers no like. */
  onLike?: (() => void) | undefined;
  /** Marks visited, or puts back; with none given the row offers no visited. */
  onVisit?: (() => void) | undefined;
}) {
  const body = visited ? <s>{name}</s> : <strong>{name}</strong>;
  // Until the reader is known they cannot be told from anyone else: a count, not a name.
  const liked = likedSaid(me === null ? { votes: poi.votes } : poi, me);
  const hasFaces = poi.voters ? poi.voters.length > 0 : poi.votes > 0;
  const hasControls = onLike !== undefined || onVisit !== undefined;
  return (
    <li
      data-poi={poi.id}
      aria-label={poi.name}
      aria-current={current ? "true" : undefined}
      className={`poi-row${passed || visited ? " passed" : ""}${closing ? " closing" : ""}`}
    >
      <div className="poi-row-text">
        {open ? (
          <button
            type="button"
            className="poi-row-open"
            aria-label={`${open.label} ${poi.name}`}
            onClick={() => open.run(poi.id)}
          >
            {body}
          </button>
        ) : (
          body
        )}
        {note && <span className="small muted poi-row-note">{note}</span>}
        {error && (
          <span role="alert" className="error small">
            {error}
          </span>
        )}
      </div>
      {(hasFaces || text || hasControls) && (
        <div className="poi-row-foot">
          <Likers voters={poi.voters} votes={poi.votes} me={me} colorOf={colorOf} label={liked} />
          <span className="small poi-row-why">{text}</span>
          {hasControls && (
            <div className="poi-controls" role="group" aria-label={`${poi.name} controls`}>
              {onLike && (
                <button
                  type="button"
                  className="poi-vote"
                  aria-pressed={poi.voted}
                  aria-label={`Like ${poi.name}`}
                  disabled={busy}
                  onClick={onLike}
                >
                  <Heart size={20} strokeWidth={1.75} aria-hidden="true" />
                </button>
              )}
              {onVisit && (
                <button
                  type="button"
                  className="poi-tick"
                  aria-label={visited ? `Put ${poi.name} back` : `Mark ${poi.name} visited`}
                  aria-busy={busy || undefined}
                  aria-disabled={settling || undefined}
                  disabled={busy}
                  onClick={() => {
                    if (!settling) onVisit();
                  }}
                >
                  {visited ? (
                    <Undo2 size={20} strokeWidth={1.75} aria-hidden="true" />
                  ) : (
                    <Check size={22} strokeWidth={2} aria-hidden="true" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
